import { StoreState } from '@deqo/foundation';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'wouter';
import { Observer, useObserver, useObserverReturningStore } from '../../CodesThatsGottaGoSomewhereElse';
import { MyBoardData } from '../../api/endpoints/boards';
import { Icon } from '../../concepts/resources/Icon';
import { useSubmitter } from '../../platform/Hook';
import { LabelValue } from '../../platform/types/LabelValue';
import { LoadingFrame } from '../../ui/LoadingGuard';
import { MenuButton } from '../../ui/MenuButton';
import { Roundings } from '../../ui/Standards';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { BoardUsageModel } from '../account/BoardUsageModel';
import { NavigationModel } from '../account/navigation/NavigationModel';
import { useFeatureCheck } from '../common/FeatureElements';
import { usePermissionCheck, usePermissions } from '../common/PermissionElements';
import { SortMode } from '../common/Sorter';
import { BoardParticipantPreview } from '../dashboard/elements/BoardParticipantPreview';
import { BoardThumb } from '../dashboard/elements/BoardThumb';
import { ContentGrid, Flexer, Spacer, Title } from '../dashboard/elements/LayoutElements';
import { XmasLightsDashboard } from '../occasions/xmas/XmasLightsDashboard';
import { UpgradeTextButton } from '../upgrade/UpgradeButton';
import { SortModeSelector } from '../workspace/SortModeSelector';
import { Colors, Input } from './../../ui';
import { AllBoardModel } from './AllBoardModel';
import { AllBoardsActionMenu } from './AllBoardsActionMenu';
import { AllBoardsActiveViewModel } from './AllBoardsActiveViewModel';
import { DateBucketView } from './DateBucketView';
import { useScrollingCallback } from './Scroller';

const { Item, ItemButton, ItemHeading, ItemBorder, ItemInfo } = ContentGrid;

const formatIfDate = (date: unknown): any => date instanceof Date ? date.toLocaleDateString() : date;

const modeOptions = [
    LabelValue('Just my boards', 'visited'),
    LabelValue('Everyone\'s boards', 'all'),
];

const lookupMode = (value: string) => modeOptions.find(x => x.value === value);

const sortOptions = [
    LabelValue('Last visited', 'lastSeenAt'),
    LabelValue('Last updated', 'updatedAt'),
    LabelValue('Date created', 'createdAt'),
    LabelValue('Alphabetical', 'label'),
];

const sortProps = [
    LabelValue('Visited', 'lastSeenAt'),
    LabelValue('Updated', 'updatedAt'),
    LabelValue('Created', 'createdAt'),
];

const sortModeToProp = (sort: SortMode) => sortProps.find(x => x.value === sort[0]);

const SortValueInfo = React.memo<{ board: MyBoardData, sort: SortMode }>(({ board, sort }) => {
    const value = formatIfDate(board[sort[0]]);
    if (!value) return null;
    switch (sort[0]) {
        // case 'relevance': return <RelevanceIndicator value={value} />;
        case 'lastSeenAt': return <span>Visited {value}</span>;
        case 'updatedAt': return <span>Updated {value}</span>;
        case 'createdAt': return <span>Created {value}</span>;
        default: return null;
    }
});

const ItemLabel = styled.div`
    position: relative;
    flex: 1 1 auto;
    height: 1.1rem;

    & > * {
        position: absolute;
        inset: 0 .75rem 0 0;
        display: block;
    }

    & span {
        display: block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & a:hover {
        text-decoration: underline;
    }
`;

const WorkspaceLabel = React.memo<{ board: StoreState<AllBoardModel> }>(({ board }) => {

    const workspace = useObserver(useResolve(NavigationModel))
        ?.workspaces?.find(x => x.id === board.workspaceId);

    const mode = (
        (workspace?.type === 'default' ? 'none' : null) ??
        (board.workspaceType === 'user' && !!workspace ? 'mine' : null) ??
        (board.workspaceType === 'user' && !workspace ? 'theirs' : null) ??
        (board.workspaceType === 'team' ? 'normal' : null) ??
        ('null')
    );

    if (mode === 'null') return null;

    return <ItemLabel title={board.workspaceType === 'team' ? board.workspaceLabel : null}>
        {mode === 'none' && <span>not in a workspace</span>}
        {mode === 'theirs' && <span>in someone&apos;s private boards</span>}
        {mode === 'mine' && <Link to={`~/${board.workspaceId}`}>
            <span></span>
        </Link>}
        {mode === 'normal' && <Link to={`~/${board.workspaceId}`}>
            <span>in {board.workspaceLabel}</span>
        </Link>}
    </ItemLabel >;

});

const ViewOnlyMessage = styled.div`
    opacity: 0;
    color: white;
    pointer-events: none;
`;

const ViewOnlyLabel = styled.div`
    position: absolute;
    left: .5rem;
    top: .5rem;
    padding: 0 .25rem;
    border-radius: 0.25rem;
    background: ${Colors.gray400};
    color: white;
    font-size: .875rem;
    font-weight: 500;
    text-align: center;
    pointer-events: none;
`;

const ViewOnlyCover = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    inset: 0;
    padding: 1.5rem;
    background-color: rgba(0,0,0,0);

    &:hover {
        background-color: rgba(0,0,0,0.5);

        ${ViewOnlyMessage} {
            opacity: 1;
        }
        ${ViewOnlyLabel} {
            opacity: 0;
            transition: none;
        }
    }
`;


const UsageMessage = styled.div`
    display: flex;
    align-items: center;
    padding: .5rem .75rem;
    border-radius: ${Roundings.md};
    background: ${Colors.hover};
    gap: .5rem;

    a { 
        text-decoration: underline;
    }
    
`;


const BoardLayout = React.memo<{ label?: string, items: AllBoardModel[], sort: SortMode }>(({ label, items, sort }) => {

    const { workspaces } = useObserver(useResolve(NavigationModel));
    const usage = useObserverReturningStore(useResolve(BoardUsageModel));
    const workspacesEnabled = useFeatureCheck('workspaces');
    const [daRead, daWrite] = usePermissions('da:read', 'da:write');

    const getEditOpts = useCallback((bs: AllBoardModel) => {
        if (!daRead) return 'hide';
        if (!daWrite) return 'disable';
        if (!workspaces.some(x => x.id === bs.workspaceId)) return 'disable';
        return 'show';
    }, [workspaces, daRead, daWrite]);

    if (!items.length) return null;

    return <Flexer margin="0 0 1.5 0">
        <ContentGrid>
            {label && <Flexer margin="0 1.4 -.5">
                <Title flush>{label}</Title>
            </Flexer>}
            {items.map(b => <Observer key={`${b.id}:${sort.join(':')}`} store={b} render={bs =>
                <Item>
                    <ItemHeading>
                        {bs.label}
                    </ItemHeading>
                    <ItemButton>
                        <AllBoardsActionMenu board={bs} editOpts={getEditOpts(b)} />
                    </ItemButton>
                    <Link to={`~/${bs.id}`}>
                        <ItemBorder>
                            <BoardThumb info={bs} />
                            <BoardParticipantPreview boardId={bs.id} />
                            {!usage.isBoardActive(bs.id) &&
                                <ViewOnlyCover>
                                    <ViewOnlyLabel>View Only</ViewOnlyLabel>
                                    <ViewOnlyMessage>
                                        <Flexer>On the Free Plan, only the {usage.limit} most recently-created boards are active.</Flexer>
                                        <Flexer height=".5" />
                                        <Flexer>All other boards become view only.</Flexer>
                                    </ViewOnlyMessage>
                                </ViewOnlyCover>
                            }
                        </ItemBorder>
                    </Link>
                    <ItemInfo>
                        <Flexer grow flex="row-reverse" justify="space-between">
                            <SortValueInfo board={bs} sort={sort} />
                            {daRead && workspacesEnabled && <WorkspaceLabel board={bs} />}
                        </Flexer>
                    </ItemInfo>
                </Item>
            } />)}
        </ContentGrid>
    </Flexer>;
});

const TopLevelBoardLayout = React.memo<{ items: AllBoardModel[], sort: SortMode }>(({ items, sort }) =>
    sort[0].endsWith('At') ? (
        <DateBucketView items={items} using={sortModeToProp(sort)} order={sort[1]} render={x => {
            return <BoardLayout label={x.label} items={x.items as any} sort={sort} />;
        }
        } />
    ) : (
        <BoardLayout items={items} sort={sort} />
    )
);

const EmptyLayout = () => {
    const [daWrite] = usePermissions('da:write');
    const account = useResolve(AccountModel);
    return <Flexer flex grow center userSelect="none" gap=".5">
        <Title color={Colors.gray400} flush size="3xl">You&apos;ve not {!!daWrite && <span>created or</span>} joined any boards yet!</Title>
        {!account.legacy ? (
            <Title color={Colors.gray400} flush size="lg">
                When you do, they will show up here.
            </Title>
        ) : (
            <Title color={Colors.gray400} flush size="lg">
                Looking for your v1 boards? Go to <Link style={{ textDecoration: 'underline' }} to="~/legacy">Legacy Boards</Link>.
            </Title>
        )}
    </Flexer>;
};


const NoResultsLayout = () =>
    <Flexer inset flex center pointerOff gap=".5">
        <Title color={Colors.gray400} flush size="3xl">We couldn&apos;t find anything matching your search.</Title>
        <Title color={Colors.gray400} flush size="lg">Not sure what else to say really...</Title>
    </Flexer>;

type ViewActions =
    readonly ['search', string] |
    readonly ['sort', SortMode] |
    readonly ['extend'];

export const AllBoardsView = () => {

    const model = useResolve(AllBoardsActiveViewModel);
    const usage = useResolve(BoardUsageModel);
    const daRead = usePermissionCheck('da:read');

    const [onAction, working] = useSubmitter(async ([action, param]: ViewActions) => {
        if (action === 'search') return await model.refresh({ search: param });
        if (action === 'sort') return await model.refresh({ sort: param });
        if (action === 'extend') return await model.extend();
    }, []);

    const { items, search, sort } = useObserver(model);

    useScrollingCallback(({ scrollTop, scrollHeight, clientHeight }) => {
        if (working) return;
        const rem = Math.abs(scrollHeight - (scrollTop + clientHeight));
        const hit = rem >= 0 && rem < 50;
        if (hit) onAction(['extend']);
    }, [onAction, working]);

    return <Flexer flex grow>
        <XmasLightsDashboard />
        <Flexer flex="row" align="center" justify="space-between" margin="0 0 3" gap="1">
            <Flexer flex="row" align="center" gap="1">
                <Flexer data-tour="ab-search" width="20">
                    <Input autoFocus placeholder="Search" value={search} onChange={x => onAction(['search', x])} />
                </Flexer>
                {usage.enabled && <>
                    <UsageMessage>
                        <Icon size="1.25" src="deqo://icons/hero/exclamation-circle" />
                        <Flexer>
                            On the Free Plan you can only edit your 4 latest boards.
                            &nbsp;&nbsp;<UpgradeTextButton>See Plans</UpgradeTextButton>
                        </Flexer>
                    </UsageMessage>
                </>}
            </Flexer>
            <Flexer data-tour="ab-filters" flex="row" align="center">
                <Spacer mode="x" f={1} />
                <Flexer flex="row" width="10">
                    {!!search ? (
                        <MenuButton disabled label="Relevance" />
                    ) : (
                        <SortModeSelector options={sortOptions} value={sort} onChange={x => onAction(['sort', x])} />
                    )}
                </Flexer>
            </Flexer>
        </Flexer>
        {!working && items.length === 0 && (
            !!search ? <NoResultsLayout /> : <EmptyLayout />
        )}
        <TopLevelBoardLayout {...{ items, sort: !search ? sort : ['relevance', 'desc'] }} />
        {working && (
            <LoadingFrame />
        )}
    </Flexer>;
};
